import { useEffect, useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Briefcase, Layers, Check, Plus } from "lucide-react";

export function TagSingleSelect({ tags, value, onChange, required }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = value ? tags.find((t) => t.id === value) : null;
  const query = search.trim().toLowerCase();
  const filtered = tags.filter((t) => !query || t.name.toLowerCase().includes(query));
  const missing = required && !selected;
  const pick = (id) => { onChange(id === value ? null : id); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-9 px-3 rounded-md border text-sm flex items-center gap-2 hover:bg-slate-50 w-full min-w-0 ${
            missing ? "border-amber-300" : "border-slate-200"
          }`}
          data-testid="entry-tag-picker"
          title={missing ? "Category is required" : ""}
        >
          {selected ? (
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: selected.color }}
                  data-testid={`entry-tag-pill-${selected.id}`} />
          ) : (
            <Layers className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          )}
          <span className={`truncate ${missing ? "text-amber-600" : (selected ? "" : "text-slate-400")}`}>
            {selected ? selected.name : `Category${required ? " *" : ""}`}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <input autoFocus value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Filter categories…"
               className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none mb-2"
               data-testid="entry-tag-search" />
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">
              {tags.length === 0 ? "No categories yet — create one on the Categories page." : "No matches."}
            </div>
          ) : filtered.map((t) => {
            const isOn = value === t.id;
            return (
              <button
                key={t.id}
                onClick={() => pick(t.id)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                data-testid={`entry-tag-option-${t.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 truncate">{t.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ClientMultiSelect({ clients, value, onChange, onCreate }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = (value || []).map((id) => clients.find((c) => c.id === id)).filter(Boolean);
  const query = search.trim();
  const filtered = clients.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const exact = clients.find((c) => c.name.toLowerCase() === query.toLowerCase());

  const toggle = (id) => {
    const next = (value || []).includes(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange(next);
  };

  const createNew = async () => {
    if (!query || exact || !onCreate) return;
    const created = await onCreate({ name: query, color: "#145c8c" });
    if (created?.id) { onChange([...(value || []), created.id]); setSearch(""); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-9 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5 flex-wrap text-left"
          data-testid="entry-client-picker"
        >
          <Briefcase className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          {selected.length === 0 ? (
            <span className="text-slate-400">Client</span>
          ) : selected.map((c) => (
            <span key={c.id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium"
                  style={{ background: `${c.color}1a`, color: c.color }}
                  data-testid={`entry-client-pill-${c.id}`}>
              {c.name}
            </span>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <input autoFocus value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search or create…"
               className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none mb-2"
               data-testid="entry-client-search" />
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 && !query ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No clients yet.</div>
          ) : filtered.map((c) => {
            const isOn = (value || []).includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                data-testid={`entry-client-option-${c.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                <span className="flex-1 truncate">{c.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
        {query && !exact && onCreate && (
          <button
            onClick={createNew}
            className="w-full mt-1 px-2 py-1.5 text-sm rounded text-[#145c8c] hover:bg-slate-50 flex items-center gap-2"
            data-testid="entry-client-create"
          >
            <Plus className="h-3.5 w-3.5" />
            Create &ldquo;{query}&rdquo;
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function MetaSelect({ items, value, onChange, onCreate, defaultColor, icon: Icon, placeholder, testId }) {
  const sel = items.find((i) => i.id === value);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const query = search.trim();
  const filtered = items.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()));
  const exact = items.find((i) => i.name.toLowerCase() === query.toLowerCase());
  useEffect(() => { if (open) setSearch(""); }, [open]);

  const handleCreate = async () => {
    if (!query || exact || !onCreate) return;
    try {
      const created = await onCreate({ name: query, color: defaultColor });
      if (created?.id) onChange(created.id);
      setOpen(false);
    } catch { /* surfaced upstream */ }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length === 1) { onChange(filtered[0].id); setOpen(false); }
      else if (query && !exact) handleCreate();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
                className="h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-2 flex-1 min-w-0">
          {sel ? (
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: sel.color }} />
          ) : (
            <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          )}
          <span className="truncate">{sel ? sel.name : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <input autoFocus value={search}
               onChange={(e) => setSearch(e.target.value)}
               onKeyDown={onKeyDown}
               placeholder={`Search or create ${placeholder.toLowerCase()}…`}
               className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none mb-2"
               data-testid={`${testId}-search`} />
        <button onClick={() => { onChange(null); setOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded text-sm text-slate-500 hover:bg-slate-100">
          — None —
        </button>
        <div className="max-h-48 overflow-y-auto">
          {filtered.map((it) => (
            <button key={it.id} onClick={() => { onChange(it.id); setOpen(false); }}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm"
                    data-testid={`${testId}-item-${it.id}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />
              <span className="truncate">{it.name}</span>
            </button>
          ))}
        </div>
        {query && !exact && onCreate && (
          <button onClick={handleCreate}
                  className="w-full text-left mt-1 px-2 py-1.5 rounded hover:bg-[#e8f1f8] text-[#0b2c47] flex items-center gap-2 text-sm font-medium border-t border-slate-100 pt-2"
                  data-testid={`${testId}-create`}>
            + Create &quot;{query}&quot;
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
